import React from 'react';
import { Info } from 'lucide-react';
import { ComboboxLazy } from './ui/ComboboxLazy';
import { useMiembroSearch } from '../hooks/useMiembroSearch';
import { RadioGroup } from './ui/RadioGroup';
import { TextInput } from './ui/TextInput';

interface SelectorClientePedidoProps {
  readonly tipoCliente: 'miembro' | 'externo';
  readonly onTipoChange: (tipo: 'miembro' | 'externo') => void;
  readonly miembroId: string;
  readonly onMiembroChange: (id: string, nombre?: string) => void;
  readonly nombreExterno?: string;
  readonly onNombreExternoChange: (nombre: string) => void;
  readonly miembroReferidoId?: string;
  readonly onMiembroReferidoChange: (id: string, nombre?: string) => void;
  readonly lumisialId: string;
  readonly disabled?: boolean;
  readonly initialMiembroNombre?: string;
  readonly initialMiembroReferidoNombre?: string;
  readonly sinAsociacionMiembro?: boolean;
}

export const SelectorClientePedido: React.FC<SelectorClientePedidoProps> = ({
  tipoCliente,
  onTipoChange,
  miembroId,
  onMiembroChange,
  nombreExterno,
  onNombreExternoChange,
  miembroReferidoId,
  onMiembroReferidoChange,
  lumisialId,
  disabled = false,
  initialMiembroNombre,
  initialMiembroReferidoNombre,
  sinAsociacionMiembro = false
}) => {
  // Hook optimizado para búsqueda de miembros
  const { handleSearch: handleSearchMiembros } = useMiembroSearch(lumisialId);

  // Crear initialOption para el miembro si existe
  const initialMiembroOption = miembroId && initialMiembroNombre ? {
    value: miembroId,
    label: initialMiembroNombre
  } : undefined;

  // Crear initialOption para el miembro referido si existe
  const initialMiembroReferidoOption = miembroReferidoId && initialMiembroReferidoNombre ? {
    value: miembroReferidoId,
    label: initialMiembroReferidoNombre
  } : undefined;

  const handleTipoChange = (tipo: 'miembro' | 'externo') => {
    onTipoChange(tipo);
    if (tipo === 'miembro') {
      onNombreExternoChange('');
      onMiembroReferidoChange('');
    } else {
      onMiembroChange('');
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        Cliente <span className="text-red-500">*</span>
      </label>

      {!sinAsociacionMiembro && (
        <div className="mb-4">
          <RadioGroup
            options={[
              { value: 'miembro', label: 'Miembro' },
              { value: 'externo', label: 'Externo' },
            ]}
            value={tipoCliente}
            onChange={(v) => handleTipoChange(v as 'miembro' | 'externo')}
            disabled={disabled}
            orientation="horizontal"
          />
        </div>
      )}

      {tipoCliente === 'miembro' ? (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Seleccionar Miembro <span className="text-red-500">*</span>
          </label>
          <ComboboxLazy
            value={miembroId}
            onChange={(value, option) => onMiembroChange(value, option?.label)}
            onSearch={handleSearchMiembros}
            placeholder="Buscar miembro..."
            emptyMessage="No se encontraron miembros"
            disabled={disabled}
            allowClear
            initialOption={initialMiembroOption}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <TextInput
              label={`${sinAsociacionMiembro ? 'Nombre' : 'Nombre del Cliente'} *`}
              value={nombreExterno || ''}
              onChange={onNombreExternoChange}
              disabled={disabled}
              placeholder="Ingrese nombre completo del cliente"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Miembro Referido (Opcional)
            </label>
            <ComboboxLazy
              value={miembroReferidoId || ''}
              onChange={(value, option) => onMiembroReferidoChange(value, option?.label)}
              onSearch={handleSearchMiembros}
              placeholder="Buscar referido..."
              emptyMessage="No se encontraron miembros"
              disabled={disabled}
              allowClear
              initialOption={initialMiembroReferidoOption}
            />
            <div className="flex items-start gap-2 mt-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">
                Contacto para consultas sobre el pedido
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
